import { useMemo, useState } from 'react';
import { useLanguage } from '../i18n/useLanguage';
import { useActiveSection } from '../hooks/useActiveSection';
import { useHideOnScroll } from '../hooks/useHideOnScroll';
import LanguageSwitcher from './LanguageSwitcher';
import MusicToggle from './MusicToggle';
import PlaceholderImage from './PlaceholderImage';
import type { MessageKey } from '../i18n/messages';
import { socialLinks } from '../lib/socialLinks';

interface NavItem {
  href: string;
  labelKey: MessageKey;
}

const NAV_ITEMS: NavItem[] = [
  { href: '#home', labelKey: 'nav.home' },
  // { href: '#register', labelKey: 'nav.register' },
  { href: '#story', labelKey: 'nav.story' },
  { href: '#features', labelKey: 'nav.features' },
  { href: '#staff', labelKey: 'nav.staff' },
  { href: '#campaign', labelKey: 'nav.campaign' },
];

// Cream-tinted drop shadow from Figma node 1:3538.
const NAV_SHADOW =
  'shadow-[0_4px_40px_0_rgba(235,207,183,0.5),0_40px_50px_0_rgba(235,207,183,0.3)]';

export default function Header() {
  const { lang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const socials = useMemo(() => socialLinks(lang), [lang]);
  const sectionIds = useMemo(
    () => NAV_ITEMS.map((n) => n.href.slice(1)),
    [],
  );
  const active = useActiveSection(sectionIds);
  // Auto-hide when scrolling down, reveal on scroll up. Once the user is
  // past the hero (#home), also tuck the header away after ~2.5s of no
  // scrolling so it doesn't loiter over the content. Suppressed while the
  // mobile menu is open so it doesn't slide away mid-interaction.
  const pastHero = active !== null && active !== 'home';
  const hidden = useHideOnScroll({
    threshold: 6,
    topOffset: 80,
    idleMs: 1200,
    enableIdle: pastHero,
  });
  // When tucked away the header still exposes a 10px "hint" peek at the
  // top of the viewport. Hovering anywhere on the header (including that
  // peek) holds it in the fully-revealed state until the cursor leaves.
  const [hovered, setHovered] = useState(false);
  const shouldHide = hidden && !open && !hovered;

  return (
    <header
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`fixed inset-x-0 top-0 z-40 bg-white transition-transform duration-300 ease-out will-change-transform ${NAV_SHADOW} ${shouldHide ? '-translate-y-[calc(100%-20px)]' : 'translate-y-0'}`}
    >
      <div className="mx-auto flex h-12 max-h-[64px] items-center justify-between gap-4 px-6 md:px-10 lg:h-[80px] lg:gap-5 lg:px-[50px]">
        <a
          href="#home"
          className={`flex h-full items-center transition-transform duration-300 ease-out ${
            shouldHide
              ? 'translate-y-7 lg:translate-y-[40px]'
              : 'translate-y-0'
          }`}
          aria-label="I Love Cooking"
        >
          {/* Logo stays floating in place while the rest of the header
              retracts upward. The header transform is
              `-translate-y-[calc(100%-20px)]`, i.e. -(headerHeight - 20px)
              ≈ -28px (mobile h-12) / -60px (lg h-[80px]). Applying the
              exact opposite translateY here compounds with the parent
              transform so the net displacement of the logo is 0 — it
              visually anchors at its original position even though the
              white bar around it has slid out of view, giving the
              "header tucks away, logo drops out and floats" effect. */}
          <PlaceholderImage
            src="/images/logo.png"
            alt="I Love Cooking"
            hint="logo"
            className="h-10 w-auto object-contain lg:h-[48px] hover:scale-105 transition-transform duration-300 ease-out"
          />
        </a>

        <nav className="hidden flex-1 items-center justify-start gap-0 lg:flex">
          {NAV_ITEMS.map((item) => {
            const isActive = active === item.href.slice(1);
            return (
              <a
                key={item.href}
                href={item.href}
                aria-current={isActive ? 'true' : undefined}
                className={`nav-link m-0 px-2 lg:px-3 xl:px-4 2xl:px-6 pt-[20px] pb-[14px] whitespace-nowrap font-thai text-lg font-bold xl:text-2xl ${
                  isActive
                    ? 'bg-contain bg-center bg-no-repeat'
                    : ''
                }`}
                style={
                  isActive
                    ? {
                        backgroundImage:
                          "url('/images/header_topic_selected_bd.png')",
                      }
                    : undefined
                }
              >
                {t(item.labelKey)}
              </a>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <MusicToggle />
          <LanguageSwitcher />
        </div>

        {/* Mobile / tablet: music toggle + language + hamburger */}
        <div className="flex items-center gap-2 lg:hidden">
          <MusicToggle />
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
            className="inline-flex h-10 w-10 items-center justify-center"
          >
            <span className="sr-only">Menu</span>
            <img
              src="/images/icon_burger.png"
              alt=""
              aria-hidden="true"
              className="h-8 w-8 object-contain"
            />
          </button>
        </div>
      </div>

      {open && (
        <nav
          aria-label="Mobile menu"
          // Cap the drawer to the viewport height minus the header
          // (h-12 = 48px below lg) so on short screens — e.g. landscape
          // phones — the nav + social row + close button can be
          // scrolled into view instead of overflowing the bottom of
          // the screen. `overflow-y-auto` keeps the rounded bottom
          // corners on the drawer chrome while only the inner
          // content scrolls; `overscroll-contain` stops the scroll
          // from chaining into the page underneath once you hit the
          // top/bottom of the drawer.
          className="absolute inset-x-0 top-full max-h-[calc(100vh-48px)] overflow-y-auto overscroll-contain rounded-b-[20px] border-x-[3px] border-b-[3px] border-white bg-section-feature shadow-[0_13px_0_0_rgba(0,0,0,0.25)] lg:hidden"
          style={{
            // Same diamond-tile pattern used by Story / Campaign / Modal
            // — keeps the drawer visually part of the same world as the
            // rest of the page rather than a generic white sheet.
            backgroundImage: "url('/images/diamond-tile.svg')",
            backgroundRepeat: 'repeat',
            backgroundSize: '64px 64px',
          }}
        >
          {/* Nav list — Figma 120:2548. Each item is centred at full
              drawer width; the active item gets the yellow-pill
              decorator from header_topic_selected_bd.png (same asset
              the desktop nav uses) so the indicator reads consistently
              across breakpoints. */}
          <ul className="flex flex-col items-center gap-3 px-6 pt-8 pb-4">
            {NAV_ITEMS.map((item) => {
              const isActive = active === item.href.slice(1);
              return (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={() => setOpen(false)}
                    aria-current={isActive ? 'true' : undefined}
                    // Type per Figma 120:2548: IBM Plex Sans Thai
                    // Bold 32px / line-height 100% / centred. Glyph
                    // outline is a 3px text-stroke that swaps colour
                    // between active (#441E2B brand-brown) and
                    // resting (#8B7776 muted) — same convention the
                    // desktop `.nav-link` rule uses, just inlined
                    // here as Tailwind arbitrary properties since
                    // the mobile drawer doesn't share that class.
                    // `paint-order: stroke fill` paints the stroke
                    // behind the white fill so the white interior
                    // stays readable.
                    className={
                      isActive
                        ? 'inline-block min-w-[200px] bg-contain bg-center bg-no-repeat px-12 pt-4 pb-3 text-center font-thai text-[24px] font-bold leading-none text-white [-webkit-text-stroke:3px_#441E2B] [paint-order:stroke_fill]'
                        : 'inline-block py-2 text-center font-thai text-[24px] font-bold leading-none text-white [-webkit-text-stroke:3px_#8B7776] [paint-order:stroke_fill]'
                    }
                    style={
                      isActive
                        ? {
                            backgroundImage:
                              "url('/images/header_topic_selected_bd.png')",
                          }
                        : undefined
                    }
                  >
                    {t(item.labelKey)}
                  </a>
                </li>
              );
            })}
          </ul>

          {/* Social row — Figma 120:2570. Reuses the same disc asset +
              SVG glyphs as FloatingSocialRail so the cooking-themed
              wooden buttons match across the page. */}
          {/* Social links row — matches `FloatingSocialRail`: each
              entry is a single combined PNG (disc background + glyph
              baked in), so the markup is just a transparent `<a>`
              with one `<img>` inside. The previous separate
              `social-disc.png` + SVG-glyph overlay + per-glyph
              `glyphPct` width sizing is gone — that pattern moved
              to a single asset per platform when the floating rail
              was redesigned. */}
          <ul className="flex items-center justify-center gap-8 pb-6 mt-4">
            {socials.map((s) => (
              <li key={s.label}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="relative flex aspect-square w-12 select-none transition hover:scale-110 active:scale-95"
                >
                  <img
                    src={s.glyph}
                    alt=""
                    aria-hidden="true"
                    className="aspect-square h-full w-full pointer-events-none select-none object-contain"
                    draggable={false}
                  />
                </a>
              </li>
            ))}
          </ul>

          {/* Close button (Figma 120:2580) — reuses icon-close.png so
              the cross matches the modal close button. */}
          <div className="flex items-center justify-center pb-8">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="transition hover:scale-110 active:scale-95"
            >
              <img
                src="/images/icon-close.png"
                alt=""
                aria-hidden="true"
                className="block h-10 w-10 select-none"
                draggable={false}
              />
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
